import { secureLogger } from '@/utils/secureLogger';

const CHUNK_RELOAD_STORAGE_KEY = 'sdlctracker:chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60000;

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
];

function extractErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return '';
}

export function isDynamicImportFailure(error: unknown): boolean {
  const message = extractErrorMessage(error).toLowerCase();
  if (!message) {
    return false;
  }

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function shouldThrottleReload(now: number): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  const previous = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
  if (!previous) {
    return false;
  }

  const previousTimestamp = Number.parseInt(previous, 10);
  if (!Number.isFinite(previousTimestamp)) {
    return false;
  }

  return now - previousTimestamp < CHUNK_RELOAD_COOLDOWN_MS;
}

export function attemptChunkReloadRecovery(source: string, error?: unknown): boolean {
  if (typeof window === 'undefined' || !isDynamicImportFailure(error)) {
    return false;
  }

  const now = Date.now();
  if (shouldThrottleReload(now)) {
    secureLogger.error('Dynamic import failure persisted after automatic reload', {
      source,
      message: extractErrorMessage(error),
    });
    return false;
  }

  window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));

  const url = new URL(window.location.href);
  url.searchParams.set('_chunk_retry', String(now));
  secureLogger.warn('Recovering from stale chunk by reloading page', {
    source,
    path: window.location.pathname,
  });
  window.location.replace(url.toString());
  return true;
}

export function installChunkLoadRecoveryHandlers(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    const candidate = event instanceof CustomEvent ? event.detail : event;
    attemptChunkReloadRecovery('vite:preloadError', candidate);
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (attemptChunkReloadRecovery('unhandledrejection', event.reason)) {
      event.preventDefault();
    }
  });
}
